import { assertNever } from "./obj";

/**
 * An exportable representation of tabular data, for exporting to Excel or putting in the clipboard.
 */
interface ExportableArray {
    header: Array<string> | undefined;
    content: Array<Array<string | number | null | undefined>>;
}

interface ExportMetadata {
    date: Date;
    organization: {
        name: string;
    };
    user: {
        firstName: string;
        lastName: string;
        email: string;
    };
}

function exportArrayValue(values: string[]): string {
    const shouldEscape = /(^"|,|^\s|\s$)/;
    return values
        .map((value) => {
            if (shouldEscape.test(value)) {
                return `"${value.replace(/"/g, '""')}"`;
            } else {
                return value;
            }
        })
        .join(", ");
}

type ParserState =
    | {
          type: "NORMAL";
          start: number;
      }
    | {
          type: "ESCAPED";
          start: number;
      }
    | {
          type: "NO_VALUE";
      }
    | {
          type: "END_VALUE";
      };

function parseArrayValue(str: string): string[] {
    const result: string[] = [];
    let i = 0;
    const whiteChars = new Set(["\r", "\n", "\t", "\f", "\v", " "]); // Taken from regex101.com definition of \s
    let state: ParserState = { type: "NO_VALUE" };

    while (i < str.length) {
        const char = str[i];
        switch (state.type) {
            case "NORMAL":
                if (char === ",") {
                    result.push(str.slice(state.start, i).trim());
                    state = { type: "NO_VALUE" };
                }
                break;
            case "ESCAPED":
                if (char === '"') {
                    if (str[i + 1] === '"') {
                        i += 1; // An escaped quote, skip it
                    } else {
                        result.push(
                            str.slice(state.start + 1, i).replace(/""/g, '"')
                        );
                        state = { type: "END_VALUE" };
                    }
                }
                break;
            case "NO_VALUE":
                if (char === '"') {
                    state = { type: "ESCAPED", start: i };
                } else if (!whiteChars.has(char)) {
                    // ignore white chars
                    state = { type: "NORMAL", start: i };
                }
                break;
            case "END_VALUE":
                if (char === ",") {
                    state = { type: "NO_VALUE" };
                } else if (!whiteChars.has(char)) {
                    throw new Error(
                        `Unexpected char '${char}' at pos ${i} in ${str}`
                    );
                }
                break;
            default:
                assertNever(state);
        }
        i += 1;
    }

    switch (state.type) {
        case "NORMAL":
            result.push(str.slice(state.start, i).trim());
            break;
        case "ESCAPED":
            result.push(str.slice(state.start + 1, i).replace(/""/g, '"'));
            break;
        case "NO_VALUE":
        case "END_VALUE":
            break;
        default:
            assertNever(state);
    }

    return result;
}

export {
    type ExportableArray,
    type ExportMetadata,
    exportArrayValue,
    parseArrayValue,
};
